import { z } from "zod/v4";
import {
  CameraSubject,
  ClientApiFeaturePathParams,
  SingleResponse,
  defineEndpoint,
  defineResource,
} from "@visioni/a3s-client-api";
import { isoDatetimeToDateString } from "@visioni/a3s-codecs";
import { FEATURE_NAME, OBJECT_TYPES } from "../constants/index";

export type ConfirmedDumpPathParams = ClientApiFeaturePathParams;

/** Waste category detected in a confirmed dump. */
export const ObjectType = z.enum(OBJECT_TYPES);
export type ObjectType = z.output<typeof ObjectType>;

/**
 * Body for confirming a dump. `timestamp` is the clip start time (the clip
 * identity). `detectedAt`/`detectedBy` are intentionally absent — the server
 * stamps them from the authenticated actor and clock, never the client.
 *
 * `description` is mandatory: reviewers must describe what was detected so the
 * customer can identify the dump in the clip, so every persisted record carries
 * reviewer detail regardless of the chosen category.
 */
export const CreateConfirmedDumpBody = z.object({
  timestamp: isoDatetimeToDateString,
  objectType: ObjectType,
  description: z.string().trim().min(1).max(500),
});

export type CreateConfirmedDumpBodyWire = z.input<typeof CreateConfirmedDumpBody>;
export type CreateConfirmedDumpBody = z.output<typeof CreateConfirmedDumpBody>;

export const ConfirmedDumpItem = z.object({
  subject: CameraSubject,
  timestamp: z.string(),
  objectType: ObjectType,
  description: z.string().optional(),
  detectedAt: z.iso.datetime(),
  detectedBy: z.string(),
});
export type ConfirmedDumpItem = z.output<typeof ConfirmedDumpItem>;

export const CreateConfirmedDumpResponse = SingleResponse(ConfirmedDumpItem);
export type CreateConfirmedDumpResponse = z.output<typeof CreateConfirmedDumpResponse>;

export const confirmedDumpsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "confirmed-dumps",
  scope: "camera",
});

export const createConfirmedDumpEndpoint = defineEndpoint(confirmedDumpsResource, {
  method: "POST",
  bodySchema: CreateConfirmedDumpBody,
  responseSchema: CreateConfirmedDumpResponse,
});
